/**
 * About — full content surface.
 *
 * Layout-only. All copy lives in `@/content/about` so marketing can
 * edit without touching JSX. Honesty-claims rule applied: no
 * background-check / insurance claims anywhere on this page —
 * `ABOUT_TRUST.items` is the source of truth and is grep-protected
 * against those terms.
 *
 * Sections:
 *   1. HERO            — eyebrow + H1 + sub + side image
 *   2. TWO-SIDED       — split: customers \u2194 PooPartners
 *   3. TRUST & SAFETY  — 3-up band, Stripe-anchored verification
 *   4. PLUCK-FOR-POO   — short narrative on why-we-built-this
 *   5. CTA BAND        — purple band, dual CTAs
 *
 * Image slots use `<ImageOrPlaceholder>` (defined inline) — on load
 * error the component swaps to a dashed-border placeholder so the
 * layout never breaks.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Lock,
  Star,
  Heart,
  Users,
  Wallet,
} from "lucide-react";
import PublicPageShell from "@/components/PublicPageShell";
import BrandDivider from "@/components/BrandDivider";
import ContestPromoSlotOverlay from "@/components/ContestPromoSlotOverlay";
import WinnerSlotCard from "@/components/WinnerSlotCard";
import { AccentHeadline } from "@/lib/AccentHeadline";
import {
  ABOUT_HERO,
  ABOUT_MARKETPLACE,
  ABOUT_TRUST,
  ABOUT_OUR_STORY,
  ABOUT_STORY,
  ABOUT_CTA,
} from "@/content/about";

/* ---------------------------------------------------------------------------
   Icon lookup — content modules reference icons by name (string) so the
   CMS layer stays serialisable. The JSX maps names to lucide components.
   --------------------------------------------------------------------------- */
const ICONS = {
  ShieldCheck,
  Lock,
  Star,
};

/* ---------------------------------------------------------------------------
   ImageOrPlaceholder — `<picture>` with webp/jpg, falls back to a tasteful
   dashed-border placeholder if neither file exists. Keeps the layout
   stable while assets are still being prepared.
   --------------------------------------------------------------------------- */
function ImageOrPlaceholder({
  src,
  alt,
  className = "",
  aspect = "aspect-[5/6]",
  testId,
}) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className={`${aspect} rounded-[28px] border-2 border-dashed border-pp-line bg-pp-v3-surface flex items-center justify-center text-center px-6 ${className}`}
        data-testid={testId ? `${testId}-placeholder` : undefined}
      >
        <span className="pp-sm" style={{ maxWidth: "24em" }}>
          {alt}
        </span>
      </div>
    );
  }
  return (
    <picture>
      <source srcSet={`${src}.webp`} type="image/webp" />
      <img
        src={`${src}.jpg`}
        alt={alt}
        className={`block w-full ${aspect} object-cover rounded-[28px] ${className}`}
        style={{ boxShadow: "var(--pp-shadow)" }}
        onError={() => setFailed(true)}
        loading="lazy"
        data-testid={testId}
      />
    </picture>
  );
}

/* ---------------------------------------------------------------------------
   Page
   --------------------------------------------------------------------------- */
export default function About() {
  return (
    <PublicPageShell>
      {/* ============================ HERO ============================ */}
      <section data-testid="about-hero-section">
        <div className="pp-wrap grid items-center gap-10 md:grid-cols-2 md:gap-16 py-12 md:py-20">
          <div>
            <span className="pp-eyebrow" data-testid="about-hero-eyebrow">
              <span className="dot" />
              {ABOUT_HERO.eyebrow}
            </span>
            <h1 className="pp-h1 mt-6" data-testid="about-hero-headline">
              {/* Item 14 refinement (Feb 2026): phrase-accent, not
                 full-line. The prior JSX painted the entire second
                 sentence via `<span className="acc">…</span>`; the
                 refined rule caps each headline at ONE phrase accent.
                 The `<AccentHeadline>` helper narrows the accent to
                 `ABOUT_HERO.headline_accent` (a PHRASE within
                 `headline_primary`), and `headline_suffix` renders as
                 plain slate — no color — so the second sentence is
                 readable but visually recedes behind the accent
                 phrase. */}
              <AccentHeadline
                text={ABOUT_HERO.headline_primary}
                accent={ABOUT_HERO.headline_accent}
              />
              {ABOUT_HERO.headline_suffix ? (
                <> {ABOUT_HERO.headline_suffix}</>
              ) : null}
            </h1>
            <p className="pp-sub mt-5" data-testid="about-hero-sub">
              {ABOUT_HERO.intro}
            </p>
          </div>
          <div
            className="flex justify-center md:justify-end"
            data-testid="about-hero-image-wrap"
          >
            <div className="relative w-full max-w-[560px]">
              <ImageOrPlaceholder
                src={ABOUT_HERO.image_src}
                alt={ABOUT_HERO.image_alt}
                testId="about-hero-image"
              />
              {/* Contest Promo overlay (2026-07-21 dispatch, Part 2 slot 2
                  of 2). Same activation rules as the landing-hero slot —
                  active contest AND no winner photo yet. `hasWinnerPhoto`
                  is hard-false pre-launch. */}
              <ContestPromoSlotOverlay
                hasWinnerPhoto={false}
                testIdSuffix="about-hero"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ====================== TWO-SIDED MARKETPLACE ====================== */}
      <section
        style={{ background: "var(--pp-v3-surface)" }}
        data-testid="about-marketplace-section"
      >
        <div className="pp-wrap py-16 md:py-24">
          <div className="max-w-2xl">
            <span className="pp-kick" data-testid="about-marketplace-kick">
              {ABOUT_MARKETPLACE.kick}
            </span>
            <h2 className="pp-h2 mt-3" data-testid="about-marketplace-headline">
              <AccentHeadline
                text={ABOUT_MARKETPLACE.headline}
                accent={ABOUT_MARKETPLACE.headline_accent}
              />
            </h2>
            <p
              className="pp-customer-p mt-4"
              data-testid="about-marketplace-body"
            >
              {ABOUT_MARKETPLACE.body}
            </p>
          </div>

          <div className="mt-12 grid items-start gap-10 md:grid-cols-2 md:gap-12">
            {/* Partner-first order: For PooPartners card sits on the
                LEFT so recruiting traffic lands on their card first.
                For dog owners is the second card. */}
            {/* For PooPartners */}
            <div
              className="rounded-[20px] border border-pp-line bg-pp-v3-bg p-8"
              style={{ boxShadow: "var(--pp-shadow)" }}
              data-testid="about-card-partners"
            >
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex items-center justify-center rounded-[10px]"
                  style={{
                    width: 40,
                    height: 40,
                    background: "rgba(242,151,43,0.15)",
                  }}
                  aria-hidden="true"
                >
                  <Wallet
                    className="h-5 w-5"
                    style={{ color: "var(--pp-orange)" }}
                    strokeWidth={2}
                  />
                </span>
                <h3 className="text-[20px] font-bold text-pp-slate">
                  {ABOUT_MARKETPLACE.partner_card.title}
                </h3>
              </div>
              <ul className="mt-5 space-y-3 list-none m-0 p-0">
                {ABOUT_MARKETPLACE.partner_card.bullets.map((line, i) => (
                  <li
                    key={i}
                    className="flex gap-3 text-[15px] text-pp-slate leading-relaxed"
                    data-testid={`about-partner-bullet-${i + 1}`}
                  >
                    <ArrowRight
                      className="h-4 w-4 mt-1 shrink-0 text-pp-orange"
                      aria-hidden="true"
                    />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={ABOUT_MARKETPLACE.partner_card.cta_to}
                className="pp-btn pp-btn-primary mt-8"
                data-testid="about-partner-cta"
              >
                {ABOUT_MARKETPLACE.partner_card.cta_label}
                <ArrowRight className="arrow h-4 w-4" aria-hidden="true" />
              </Link>
            </div>

            {/* For dog owners */}
            <div
              className="rounded-[20px] border border-pp-line bg-pp-v3-bg p-8"
              style={{ boxShadow: "var(--pp-shadow)" }}
              data-testid="about-card-customers"
            >
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex items-center justify-center rounded-[10px] bg-pp-lavender"
                  style={{ width: 40, height: 40 }}
                  aria-hidden="true"
                >
                  <Heart
                    className="h-5 w-5"
                    style={{ color: "var(--pp-lavender-text)" }}
                    strokeWidth={2}
                  />
                </span>
                <h3 className="text-[20px] font-bold text-pp-slate">
                  {ABOUT_MARKETPLACE.customer_card.title}
                </h3>
              </div>
              <ul className="mt-5 space-y-3 list-none m-0 p-0">
                {ABOUT_MARKETPLACE.customer_card.bullets.map((line, i) => (
                  <li
                    key={i}
                    className="flex gap-3 text-[15px] text-pp-slate leading-relaxed"
                    data-testid={`about-customer-bullet-${i + 1}`}
                  >
                    <ArrowRight
                      className="h-4 w-4 mt-1 shrink-0 text-pp-orange"
                      aria-hidden="true"
                    />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={ABOUT_MARKETPLACE.customer_card.cta_to}
                className="pp-btn pp-btn-primary mt-8"
                data-testid="about-customer-cta"
              >
                {ABOUT_MARKETPLACE.customer_card.cta_label}
                <ArrowRight className="arrow h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>

          <div className="mt-16 grid items-center gap-10 md:grid-cols-2 md:gap-16">
            <div className="order-2 md:order-1 max-w-[560px]">
              <ImageOrPlaceholder
                src={ABOUT_MARKETPLACE.cut_image_src}
                alt={ABOUT_MARKETPLACE.cut_image_alt}
                aspect="aspect-[4/3]"
                testId="about-marketplace-image"
              />
            </div>
            <div className="order-1 md:order-2">
              <h3 className="text-[22px] md:text-[26px] font-bold text-pp-slate leading-snug">
                <AccentHeadline
                  text={ABOUT_MARKETPLACE.cut_headline}
                  accent={ABOUT_MARKETPLACE.cut_headline_accent}
                />
              </h3>
              <p
                className="pp-customer-p mt-4"
                data-testid="about-marketplace-cut-body"
              >
                {ABOUT_MARKETPLACE.cut_body}
              </p>
              <p
                className="pp-sm mt-3"
                data-testid="about-marketplace-cut-note"
              >
                {ABOUT_MARKETPLACE.cut_note_prefix}
                <Link
                  to={ABOUT_MARKETPLACE.cut_note_link_to}
                  className="font-semibold text-pp-orange hover:underline"
                  data-testid="about-marketplace-pricing-link"
                >
                  {ABOUT_MARKETPLACE.cut_note_link_label}
                </Link>
                {ABOUT_MARKETPLACE.cut_note_suffix}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========================= TRUST & SAFETY ========================= */}
      <section data-testid="about-trust-section">
        <div className="pp-wrap py-16 md:py-24">
          <div className="max-w-2xl">
            <span className="pp-kick" data-testid="about-trust-kick">
              {ABOUT_TRUST.kick}
            </span>
            <h2 className="pp-h2 mt-3" data-testid="about-trust-headline">
              <AccentHeadline
                text={ABOUT_TRUST.headline}
                accent={ABOUT_TRUST.headline_accent}
              />
            </h2>
            <p className="pp-customer-p mt-4" data-testid="about-trust-body">
              {ABOUT_TRUST.body}
            </p>
          </div>

          <ul className="mt-12 grid gap-6 md:grid-cols-3 md:gap-8 list-none m-0 p-0">
            {ABOUT_TRUST.items.map((item, i) => {
              const Icon = ICONS[item.icon] || ShieldCheck;
              return (
                <li
                  key={item.key}
                  className="rounded-[20px] border border-pp-line bg-pp-v3-bg p-7"
                  style={{ boxShadow: "var(--pp-shadow)" }}
                  data-testid={`about-trust-item-${i + 1}`}
                >
                  <span
                    className="inline-flex items-center justify-center rounded-[10px] bg-pp-lavender"
                    style={{ width: 40, height: 40 }}
                    aria-hidden="true"
                  >
                    <Icon
                      className="h-5 w-5"
                      style={{ color: "var(--pp-lavender-text)" }}
                      strokeWidth={2}
                    />
                  </span>
                  <h3 className="mt-5 text-[18px] font-bold text-pp-slate">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-[15px] text-pp-text-soft leading-relaxed">
                    {item.body}
                  </p>
                  <span
                    className="mt-4 inline-block text-[12px] font-bold uppercase tracking-[0.08em] text-pp-orange"
                    data-testid={`about-trust-note-${i + 1}`}
                  >
                    {item.note}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ===================== OUR STORY (overview) ===================== */}
      <section
        style={{ background: "var(--pp-v3-surface)" }}
        data-testid="about-our-story-section"
      >
        <div className="pp-wrap py-16 md:py-24">
          <div className="max-w-3xl">
            <span className="pp-kick" data-testid="about-our-story-kick">
              {ABOUT_OUR_STORY.kick}
            </span>
            <div
              className="pp-customer-p mt-5 space-y-4"
              data-testid="about-our-story-body"
            >
              {ABOUT_OUR_STORY.paragraphs.map((p, i) => (
                <p key={i} data-testid={`about-our-story-paragraph-${i + 1}`}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== PLUCK-FOR-POO STORY ===================== */}
      <BrandDivider testId="about-divider-before-story" />
      <section
        style={{ background: "var(--pp-v3-surface)" }}
        data-testid="about-story-section"
      >
        <div className="pp-wrap py-16 md:py-24 grid items-start gap-10 md:grid-cols-[1.2fr_1fr] md:gap-16">
          <div>
            <span className="pp-kick" data-testid="about-story-kick">
              {ABOUT_STORY.kick}
            </span>
            <h2 className="pp-h2 mt-3" data-testid="about-story-headline">
              <AccentHeadline
                text={ABOUT_STORY.headline}
                accent={ABOUT_STORY.headline_accent}
              />
            </h2>
            <div
              className="pp-customer-p mt-5 space-y-4"
              data-testid="about-story-body"
            >
              {ABOUT_STORY.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
          <aside
            className="rounded-[20px] border border-pp-line bg-pp-v3-bg p-7"
            style={{ boxShadow: "var(--pp-shadow)" }}
            data-testid="about-story-pullquote"
          >
            <span
              className="inline-flex items-center justify-center rounded-[10px] bg-pp-lavender"
              style={{ width: 40, height: 40 }}
              aria-hidden="true"
            >
              <Users
                className="h-5 w-5"
                style={{ color: "var(--pp-lavender-text)" }}
                strokeWidth={2}
              />
            </span>
            <p className="mt-5 text-[18px] font-semibold text-pp-slate leading-snug">
              {ABOUT_STORY.pullquote}
            </p>
            <p className="mt-4 text-[14px] text-pp-text-soft">
              {ABOUT_STORY.pullquote_attrib}
            </p>
          </aside>
        </div>
      </section>

      {/* ============================ CTA BAND ============================ */}
      <BrandDivider testId="about-divider-before-cta" />
      <section
        style={{ background: "var(--pp-purple)" }}
        data-testid="about-cta-section"
      >
        <div className="pp-wrap py-16 md:py-20 text-center">
          <h2
            className="text-white font-extrabold text-[28px] md:text-[40px] leading-tight max-w-3xl mx-auto"
            data-testid="about-cta-headline"
          >
            {ABOUT_CTA.headline}
          </h2>
          <p
            className="mt-4 text-white/85 text-[16px] md:text-[18px] max-w-xl mx-auto"
            data-testid="about-cta-sub"
          >
            {ABOUT_CTA.sub}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to={ABOUT_CTA.customer_to}
              className="pp-btn"
              style={{
                background: "white",
                color: "var(--pp-slate-2)",
              }}
              data-testid="about-cta-customer"
            >
              {ABOUT_CTA.customer_label}
            </Link>
            <Link
              to={ABOUT_CTA.partner_to}
              className="pp-btn pp-btn-primary"
              data-testid="about-cta-partner"
            >
              {ABOUT_CTA.partner_label}
              <ArrowRight className="arrow h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
      <WinnerSlotCard winnerRank={6} />
    </PublicPageShell>
  );
}
